import { Component, ChangeDetectionStrategy, inject, signal, computed, OnInit, DestroyRef } from '@angular/core';

import { RouterModule, Router, NavigationEnd, Event } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { LayoutService } from '../../core/services/layout.service';
import { AuthService } from '../../core/services/auth.service';
import { UserService } from '../../core/services/user.service';
import { AccessControlService } from '../../core/services/access-control.service';
import { MENU_CONFIG } from '../../core/constants/menu.constants';
import { ICONS } from '../../core/constants/icon.constants';
import { ROLES } from '../../core/constants/role.constants';

import { ButtonComponent } from '../../shared/components/button/button.component';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { UserSettingsModalComponent } from '../../shared/components/user-settings-modal/user-settings-modal.component';

@Component({
    selector: 'fk-sidebar',
    standalone: true,
    imports: [RouterModule, MatIconModule, MatTooltipModule, CommonModule, ButtonComponent, ModalComponent, UserSettingsModalComponent],
    templateUrl: './sidebar.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SidebarComponent implements OnInit {
    layoutService = inject(LayoutService);
    authService = inject(AuthService);
    private userService = inject(UserService);
    private router = inject(Router);
    private accessControlService = inject(AccessControlService);
    private destroyRef = inject(DestroyRef);

    iconConstants = ICONS;
    isLogoutModalOpen = signal(false);
    isUserSettingsOpen = signal(false);
    currentUrl = signal('');

    readonly appVersion = '3.0';

    menuItems = computed(() => {
        const isSuperAdmin = this.accessControlService.hasRole(ROLES.SUPER_ADMIN);
        return MENU_CONFIG.filter(item => {
            if (item.roles && !this.accessControlService.hasAnyRole(item.roles)) return false;
            if (item.adminOnly && !isSuperAdmin && !this.accessControlService.isAuthorizedAdmin()) return false;
            return true;
        });
    });

    ngOnInit() {
        this.currentUrl.set(this.router.url);
        this.router.events.pipe(
            filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((event: NavigationEnd) => {
            this.currentUrl.set(event.urlAfterRedirects);
        });

        this.userService.getAllRoles().subscribe();
    }

    getRoleLabel(role: string): string {
        const found = this.userService.allRoles().find(r => r.role_code === role || r.role_id === role);
        if (found) {
            return found.role_name;
        }

        // Fallback mapping for core role codes
        const fallbacks: Record<string, string> = {
            'PLATFORM_SUPER_ADMIN': 'Platform Super Admin',
            'ORG_ADMIN': 'Admin',
            'CS': 'CS',
            'CUSTOMER': 'Customer'
        };
        return fallbacks[role] || role;
    }

    isRouteActive(item: any): boolean {
        if (!item.route) return false;
        const currentUrl = this.currentUrl();
        
        // Exact match or query parameter match or child route match
        if (currentUrl === item.route || currentUrl.startsWith(item.route + '?') || currentUrl.startsWith(item.route + '/')) {
            return true;
        }
        
        // Special case: Inspections menu item should match any sub-routes starting with /inspections
        if (item.route === '/inspections/new' && currentUrl.startsWith('/inspections')) {
            return true;
        }
        
        // Special case: Audit Logs menu item covers driver-logs and load-logs
        if (item.route === '/audit-logs') {
            return currentUrl.startsWith('/driver-logs') || currentUrl.startsWith('/load-logs');
        }
        
        return false;
    }

    openUserSettings(): void {
        this.isUserSettingsOpen.set(true);
    }

     getUserInitials(name: string): string {
        if (!name) return '';

        const words = name.trim().split(/\s+/);

        if (words.length === 1) {
            return words[0].substring(0, 2).toUpperCase();
        }

        return (
            words[0].charAt(0) +
            words[words.length - 1].charAt(0)
        ).toUpperCase();
    }

    onLogout() {
        this.isLogoutModalOpen.set(true);
    }

    confirmLogout() {
        this.isLogoutModalOpen.set(false);
        this.authService.logout();
    }
}
